/**
 * Patient Form Validation Utilities
 *
 * Complex validation rules matching RA2 behavior for Patient Edit screen
 * These validations are used before form submission
 */

import { PatientFormSchemaType } from '../pages/main/patient/types';
import type { Dayjs } from 'dayjs';
import {
  AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
  BIRTH_DATE_ERROR,
  HEALTH_INSURANCE_CARD_MARK_REQUIRED,
  HEALTH_INSURER_NUMBER_FORMAT_ERROR,
  INSURED_NAME_KANA_KATAKANA_ONLY_ERROR,
  LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
  OVER_75_EARLY_ELDERLY_CERT_WARNING,
  OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
  OVER_75_NO_LATE_ELDERLY_INSURANCE_CONFIRM,
  PATIENT_COST_RATE_MAX_ERROR,
  PATIENT_NAME_KANA_REQUIRED_ERROR,
  PATIENT_NAME_REQUIRED_ERROR,
} from '../pages/main/patient/constants/validationMessages';
import { fullWidthToHalfWidthNumber } from './japaneseText';

// ==================== Date Utilities ====================

/**
 * Parse Japanese era date string to Date object
 * Handles formats like: 令和06年01月16日, 平成31年04月30日
 */
export function parseJapaneseDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Define era mappings
  const eras: Record<string, number> = {
    明治: 1868,
    大正: 1912,
    昭和: 1926,
    平成: 1989,
    令和: 2019,
  };

  // Try to match Japanese era format: 令和06年01月16日
  const eraMatch = dateStr.match(/^(明治|大正|昭和|平成|令和)(\d{1,2})年(\d{1,2})月(\d{1,2})日$/);
  if (eraMatch) {
    const [, era, yearStr, monthStr, dayStr] = eraMatch;
    const eraStartYear = eras[era];
    const year = eraStartYear + parseInt(yearStr, 10) - 1;
    const month = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    return new Date(year, month, day);
  }

  // Try ISO format: YYYY-MM-DD
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  return null;
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string | Date | Dayjs | null | undefined): number | null {
  if (!dateOfBirth) return null;

  let dob: Date | null = null;

  // Handle Dayjs objects
  if (dateOfBirth && typeof dateOfBirth === 'object' && 'toDate' in dateOfBirth) {
    dob = dateOfBirth.toDate();
  }
  // Handle Date objects
  else if (dateOfBirth instanceof Date) {
    dob = dateOfBirth;
  }
  // Handle string (Japanese date or ISO format)
  else if (typeof dateOfBirth === 'string') {
    dob = parseJapaneseDate(dateOfBirth);
  }

  if (!dob) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

/**
 * Calculate academic year grade (学年)
 * In Japan, school year starts April 1st
 */
export function calculateGakunen(dateOfBirth: string | Date | Dayjs | null | undefined): number {
  if (!dateOfBirth) return 0;

  let dob: Date | null = null;

  // Handle Dayjs objects
  if (dateOfBirth && typeof dateOfBirth === 'object' && 'toDate' in dateOfBirth) {
    dob = dateOfBirth.toDate();
  }
  // Handle Date objects
  else if (dateOfBirth instanceof Date) {
    dob = dateOfBirth;
  }
  // Handle string (Japanese date or ISO format)
  else if (typeof dateOfBirth === 'string') {
    dob = parseJapaneseDate(dateOfBirth);
  }

  if (!dob) return 0;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  // School year starts April 1st
  const schoolYear = currentMonth >= 4 ? currentYear : currentYear - 1;

  // Child enters elementary school in April of the year they turn 6
  // (specifically, born between April 2 of previous year and April 1 of current year)
  const birthYear = dob.getFullYear();
  const birthMonth = dob.getMonth() + 1;
  const birthDay = dob.getDate();

  // Determine school entry year
  let entryYear: number;
  if (birthMonth < 4 || (birthMonth === 4 && birthDay === 1)) {
    entryYear = birthYear + 6;
  } else {
    entryYear = birthYear + 7;
  }

  // Calculate grade (1 = first grade, 6 = sixth grade elementary, 7-9 = middle school, etc.)
  const grade = schoolYear - entryYear + 1;

  return grade;
}

/**
 * Calculate age at a specific date
 */
export function calculateAgeAtDate(
  dateOfBirth: string | Date | Dayjs | null | undefined,
  targetDate: string | Date | Dayjs | null | undefined
): number | null {
  if (!dateOfBirth || !targetDate) return null;

  let dob: Date | null = null;
  let target: Date | null = null;

  // Handle dateOfBirth
  if (typeof dateOfBirth === 'object' && 'toDate' in dateOfBirth) {
    dob = dateOfBirth.toDate();
  } else if (dateOfBirth instanceof Date) {
    dob = dateOfBirth;
  } else if (typeof dateOfBirth === 'string') {
    dob = parseJapaneseDate(dateOfBirth);
  }

  // Handle targetDate
  if (typeof targetDate === 'object' && 'toDate' in targetDate) {
    target = targetDate.toDate();
  } else if (targetDate instanceof Date) {
    target = targetDate;
  } else if (typeof targetDate === 'string') {
    target = parseJapaneseDate(targetDate);
  }

  if (!dob || !target) return null;

  let age = target.getFullYear() - dob.getFullYear();
  const monthDiff = target.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

// ==================== Validation Rule Interfaces ====================

export interface ValidationResult {
  valid: boolean;
  message?: string;
  confirmRequired?: boolean;
  strictOnly?: boolean; // Skip this rule in provisional mode if true
}

export type ValidationRule = (
  data: PatientFormSchemaType
) => ValidationResult | Promise<ValidationResult>;

type AppModalLike = {
  confirm?: (config: {
    title?: string;
    content?: string;
    okText?: string;
    cancelText?: string;
    onOk?: () => void;
    onCancel?: () => void;
  }) => void;
  error?: (config: { title?: string; content?: string; style?: { whiteSpace?: string } }) => void;
};

// ==================== Validation Rules (from RA2) ====================

/**
 * Rule: Patient name (11.1) is required and must be provided
 * Always validated in both official and provisional modes
 */
export const validatePatientNameRequired: ValidationRule = data => {
  const name = data.patient_information?.name?.trim();
  if (!name) {
    return {
      valid: false,
      message: PATIENT_NAME_REQUIRED_ERROR,
    };
  }
  return { valid: true };
};

/**
 * Rule: Patient name kana (11.3) is required and must be provided
 * Always validated in both official and provisional modes
 */
export const validatePatientNameKanaRequired: ValidationRule = data => {
  const nameKana = data.patient_information?.name_kana?.trim();
  if (!nameKana) {
    return {
      valid: false,
      message: PATIENT_NAME_KANA_REQUIRED_ERROR,
    };
  }
  return { valid: true };
};

/**
 * Rule: Age must not be negative (future birth date)
 * RA2: #184 保険証 生年月日入力欄に、未来の年月日が入力できて登録できてしまう
 */
export const validateAgeNotNegative: ValidationRule = data => {
  const age = calculateAge(data.patient_information.date_of_birth);
  if (age !== null && age < 0) {
    return {
      valid: false,
      message: BIRTH_DATE_ERROR,
    };
  }
  return { valid: true };
};

/**
 * Rule: Warn if under 65 with late-elderly insurance
 * RA2: No206 後期高齢で65歳以下の場合は確認
 * Note: Age validation for elderly certificates (age < 70) is handled by validateAgeForElderlyCertificate in patientTypes.ts
 */
export const validateLateElderlyInsuranceAge: ValidationRule = data => {
  const age = calculateAge(data.patient_information.date_of_birth);
  const insuranceTypeId = data.health_insurance_card.insurance_type_id;

  if (age !== null && age < 65 && insuranceTypeId === 2) {
    return {
      valid: true,
      confirmRequired: true,
      message: LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
    };
  }
  return { valid: true };
};

/**
 * Rule: If age >= 75, must select 後期高齢 insurance type
 * Confirmation required if not selected
 */
export const validateOver75LateElderly: ValidationRule = data => {
  const age = calculateAge(data.patient_information.date_of_birth);
  const insuranceTypeId = data.health_insurance_card.insurance_type_id;

  if (age !== null && age >= 75 && insuranceTypeId !== 2) {
    return {
      valid: true,
      confirmRequired: true,
      message: OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
    };
  }
  return { valid: true };
};

/**
 * Rule: Validate medical aid type matches number
 * RA2: NO122 医療助成入力補助入力で番号選択した後に、番号はそのままで「医療助成種別」を選択しなおして登録
 */
export const validateMedicalAidTypeMatchesNumber: ValidationRule = data => {
  const insurerTypeId = data.medical_aid.insurer_type_id;
  const medicalAidNumber = data.medical_aid.medical_aid_number;

  // If medical aid number is set but type is changed to "なし" (1)
  if (insurerTypeId === 1 && medicalAidNumber) {
    // This is handled by clearing fields, not blocking
    return { valid: true };
  }

  // If type is not "なし" but number suggests different type, warn
  // (This would require looking up the number's expected type from insurer master)
  return { valid: true };
};

/**
 * Rule: Warn if 75+ with early elderly insurance certificate
 * RA2: 75歳以上にもかかわらず、前期高齢者を設定している場合
 */
export const validateOver75WithEarlyElderly: ValidationRule = data => {
  const age = calculateAge(data.patient_information.date_of_birth);
  const elderlyId = data.health_insurance_card.elderly_insurance_certificate_id;
  const insuranceTypeId = data.health_insurance_card.insurance_type_id;

  if (age !== null && age >= 75) {
    // Warn if early elderly certificate is selected
    if (elderlyId && elderlyId >= 2 && elderlyId <= 4) {
      return {
        valid: true,
        confirmRequired: true,
        message: OVER_75_EARLY_ELDERLY_CERT_WARNING,
      };
    }

    // Warn if not late-elderly insurance
    if (insuranceTypeId !== 2) {
      return {
        valid: true,
        confirmRequired: true,
        message: OVER_75_NO_LATE_ELDERLY_INSURANCE_CONFIRM,
      };
    }
  }
  return { valid: true };
};

/**
 * Rule: Warn if 70-74 without early elderly certificate
 * RA2: No64 70歳以上75歳未満、前期高齢者を設定していない場合
 */
export const validateEarlyElderlyNotSet: ValidationRule = data => {
  const age = calculateAge(data.patient_information.date_of_birth);
  const elderlyId = data.health_insurance_card.elderly_insurance_certificate_id;

  if (age !== null && age >= 70 && age < 75 && (!elderlyId || elderlyId === 1)) {
    return {
      valid: true,
      confirmRequired: true,
      message: AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
    };
  }
  return { valid: true };
};

/**
 * Rule: Card mark is required for specific insurance types
 * RA2: 保険証マークは社会保険(1)と自由診療(4)で必須
 * A2 No410 parity (一般 only): if 番号 has a value, 記号 may be blank — i.e. at-least-one.
 * 自由診療 (4) keeps 記号 strictly required (A2's conditional override only targets type 1).
 */
export const validateCardMarkRequirement: ValidationRule = data => {
  const insuranceTypeId = data.health_insurance_card.insurance_type_id;
  const cardMark = data.health_insurance_card.card_mark;
  const cardNumber = data.health_insurance_card.card_number;

  if (insuranceTypeId === 1 || insuranceTypeId === 4) {
    if (!cardMark?.trim()) {
      const relaxedByCardNumber = insuranceTypeId === 1 && !!cardNumber?.trim();
      if (!relaxedByCardNumber) {
        return {
          valid: false,
          message: HEALTH_INSURANCE_CARD_MARK_REQUIRED,
          strictOnly: true,
        };
      }
    }
  }
  return { valid: true };
};

/**
 * Rule: Insurer number format validation
 * RA2: 保険者番号は6～8文字の数字
 */
export const validateInsurerNumberFormat: ValidationRule = data => {
  const insurerNumber = data.health_insurance_card.insurer_number;

  if (!insurerNumber) return { valid: true }; // Optional when empty

  const normalized = fullWidthToHalfWidthNumber(insurerNumber);

  // Must be 6-8 characters, all digits
  if (normalized.length < 6 || normalized.length > 8 || !/^\d{6,8}$/.test(normalized)) {
    return {
      valid: false,
      message: HEALTH_INSURER_NUMBER_FORMAT_ERROR,
      strictOnly: true,
    };
  }
  return { valid: true };
};

/**
 * Rule: Insured person name kana must be katakana
 * RA2: 被保険者カナはカタカナのみ
 */
export const validateInsurerNameKatakana: ValidationRule = data => {
  const nameKana = data.health_insurance_card.name_kana;

  if (!nameKana) return { valid: true }; // Optional when empty

  // Check if contains only katakana characters (and spaces)
  const katakanaRegex = /^[\u30A0-\u30FF\u31F0-\u31FF\s\-ー]*$/;
  if (!katakanaRegex.test(nameKana)) {
    return {
      valid: false,
      message: INSURED_NAME_KANA_KATAKANA_ONLY_ERROR,
      strictOnly: true,
    };
  }
  return { valid: true };
};

/**
 * Rule: Patient cost rate must not exceed 10
 * RA2: 患者負担率は10以下
 */
export const validatePatientCostRateMax: ValidationRule = data => {
  const costRate = data.health_insurance_card.patient_cost_rate;

  if (!costRate) return { valid: true }; // Optional when empty

  const numValue = parseFloat(costRate);
  if (isNaN(numValue)) return { valid: true };

  if (numValue > 10) {
    return {
      valid: false,
      message: PATIENT_COST_RATE_MAX_ERROR,
      strictOnly: true,
    };
  }
  return { valid: true };
};

// ==================== Validation Runner ====================

/**
 * All validation rules in order of execution.
 *
 * NOTE: A2 steps 1-15 (age checks, insurer number, kouki, cost rate, qual date,
 * medical aid type, and all confirmation dialogs) are now handled by
 * useCrossSectionValidation.runAllValidations() in A2-matching order.
 *
 * This array only contains RA2N-specific extra safety rules (strictOnly)
 * that did not exist in A2 FE validation.
 */
export const validationRules: ValidationRule[] = [
  // RA2N-specific extra format validations (strictOnly — skipped in provisional mode)
  validateCardMarkRequirement,
  validateInsurerNumberFormat,
  validateInsurerNameKatakana,
  validatePatientCostRateMax,
];

/**
 * Run all validation rules
 * Returns first failing rule or requires confirmation
 */
export async function runValidations(
  data: PatientFormSchemaType,
  mode: 'official' | 'provisional' = 'official',
  appModal?: AppModalLike
): Promise<{ canProceed: boolean; errors: string[] }> {
  const errors: string[] = [];
  const confirmations: string[] = [];

  for (const rule of validationRules) {
    const result = await rule(data);

    // Skip strict-only rules in provisional mode
    if (mode === 'provisional' && result.strictOnly) {
      continue;
    }

    if (!result.valid && result.message) {
      errors.push(result.message);
    } else if (result.confirmRequired && result.message) {
      confirmations.push(result.message);
    }
  }

  // If there are hard errors, return them
  if (errors.length > 0) {
    return { canProceed: false, errors };
  }

  // If there are confirmations needed, show them one by one
  for (const confirmMessage of confirmations) {
    const confirmed = await new Promise<boolean>(resolve => {
      if (appModal?.confirm) {
        appModal.confirm({
          title: '確認',
          content: confirmMessage,
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
        return;
      }

      resolve(true);
    });

    if (!confirmed) {
      return { canProceed: false, errors: [] };
    }
  }

  return { canProceed: true, errors: [] };
}

/**
 * Show validation errors
 * Only displays the first error message if multiple errors exist
 */
export function showValidationErrors(errors: string[], appModal?: AppModalLike): void {
  if (errors.length > 0) {
    appModal?.error?.({
      title: '入力エラー',
      content: errors[0],
    });
  }
}

export default {
  parseJapaneseDate,
  calculateAge,
  calculateGakunen,
  calculateAgeAtDate,
  runValidations,
  showValidationErrors,
};
